import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import {
  getProductivityExportUrl,
  syncProductivityToDeere,
  getProductivityDeereSyncStatus,
  TProductivitySyncStatus,
} from "@services/fieldsService";

const Root = styled.div`
  position: absolute;
  inset: 18px 16px auto auto;
  background: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  z-index: 2;
  width: 168px;
`;

const Title = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 12px;
  color: #899093;
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #494c5b;
`;

const Swatch = styled.span<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
  background: ${({ color }) => color};
`;

const Pct = styled.span`
  margin-left: auto;
  color: #899093;
`;

const Footer = styled.div`
  margin-top: 8px;
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #899093;
`;

const Divider = styled.div`
  height: 1px;
  background: #eef1f0;
  margin: 8px 0;
`;

const ActionLabel = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #899093;
  margin-bottom: 4px;
`;

const ExportRow = styled.div`
  display: flex;
  gap: 10px;
`;

const ExportLink = styled.a`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #68ad18;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    color: #417900;
  }
`;

const LinkButton = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #68ad18;
  text-align: left;
  &:hover {
    color: #417900;
  }
  &:disabled {
    color: #cbd5d8;
    cursor: default;
  }
`;

const StatusLine = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: 600;
  margin-top: 2px;
`;

const Regenerate = styled.button`
  margin-top: 6px;
  width: 100%;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #68ad18;
  text-align: left;
  &:hover {
    color: #417900;
  }
`;

const ProductivityLegend: React.FC = () => {
  const vm = useFieldsScreenVM();

  const fieldId = vm.selectedFieldId != null ? String(vm.selectedFieldId) : null;
  const externalId = vm.selectedField?.get("externalId") as string | undefined;
  const isJdLinked = typeof externalId === "string" && externalId.startsWith("johndeere:");
  const isActive =
    vm.selectedFieldView === "productivity" &&
    fieldId != null &&
    vm.productivityReady &&
    vm.productivityLegend.length > 0;

  const [syncStatus, setSyncStatus] = useState<TProductivitySyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load JD sync status when the productivity map for a JD-linked field is shown.
  useEffect(() => {
    if (!isActive || !isJdLinked || !fieldId) {
      setSyncStatus(null);
      return;
    }
    getProductivityDeereSyncStatus(fieldId)
      .then(setSyncStatus)
      .catch(() => setSyncStatus(null));
  }, [isActive, isJdLinked, fieldId]);

  // Poll while the layer is still processing in John Deere.
  useEffect(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    if (!fieldId || syncStatus?.status !== "QUEUED") return;
    const tick = () => {
      getProductivityDeereSyncStatus(fieldId)
        .then((s) => {
          setSyncStatus(s);
          if (s.status === "QUEUED") pollRef.current = setTimeout(tick, 6000);
        })
        .catch(() => {
          pollRef.current = setTimeout(tick, 6000);
        });
    };
    pollRef.current = setTimeout(tick, 6000);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [syncStatus?.status, fieldId]);

  const handleSync = useCallback(async () => {
    if (!fieldId) return;
    setSyncing(true);
    try {
      const res = await syncProductivityToDeere(fieldId, {
        replaceExisting: !!syncStatus?.mapLayerUri,
      });
      toast.success("Productivity map sent to John Deere.");
      setSyncStatus({ status: res.status, mapLayerUri: res.mapLayerUri });
    } catch (e: any) {
      toast.error(e?.message ?? "John Deere sync failed");
    } finally {
      setSyncing(false);
    }
  }, [fieldId, syncStatus?.mapLayerUri]);

  if (!isActive || !fieldId) return null;

  const zonePct = new Map<number, number>();
  vm.productivityStats?.zones.forEach((z) => zonePct.set(z.zone, z.percent));

  const syncLabel = (() => {
    if (syncing) return "Sending…";
    if (syncStatus?.status === "QUEUED") return "Processing in John Deere…";
    if (syncStatus?.status === "VALID") return "✓ Synced — re-send";
    if (syncStatus?.status) return "Sync failed — retry";
    return "Send to John Deere";
  })();

  return (
    <Root>
      <Title>Productivity zones</Title>
      {vm.productivityLegend.map((item) => (
        <Row key={item.zone}>
          <Swatch color={item.color} />
          <span>{item.label}</span>
          {zonePct.has(item.zone) && <Pct>{zonePct.get(item.zone)}%</Pct>}
        </Row>
      ))}
      {vm.productivityStats && (
        <Footer>
          Based on {vm.productivityStats.seasonsValid} seasons
          {vm.productivityStats.yearsUsed.length > 0 &&
            ` (${Math.min(...vm.productivityStats.yearsUsed)}–${Math.max(
              ...vm.productivityStats.yearsUsed
            )})`}
        </Footer>
      )}

      <Divider />
      <ActionLabel>Export</ActionLabel>
      <ExportRow>
        <ExportLink href={getProductivityExportUrl(fieldId, "shp")} download>
          Shapefile
        </ExportLink>
        <ExportLink href={getProductivityExportUrl(fieldId, "kml")} download>
          KML
        </ExportLink>
      </ExportRow>

      {isJdLinked && (
        <>
          <Divider />
          <LinkButton onClick={handleSync} disabled={syncing || syncStatus?.status === "QUEUED"}>
            {syncLabel}
          </LinkButton>
          {syncStatus?.status === "VALID" && (
            <StatusLine style={{ color: "#68ad18" }}>Visible in Operations Center</StatusLine>
          )}
          {syncStatus?.status && syncStatus.status !== "VALID" && syncStatus.status !== "QUEUED" && (
            <StatusLine style={{ color: "#d94c4c" }}>
              {syncStatus.error ?? "Last sync failed"}
            </StatusLine>
          )}
        </>
      )}

      <Divider />
      <Regenerate onClick={() => vm.regenerateProductivity(fieldId)}>↻ Regenerate</Regenerate>
    </Root>
  );
};

export default observer(ProductivityLegend);
